import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useTimer — tracks elapsed time with start/stop/pause/resume controls.
 * Pauses automatically on tab hide and user inactivity (configurable).
 * Audio/video playback counts as activity — listening is studying even
 * without mouse/keyboard input. On mobile, audio that keeps playing while
 * the tab is hidden (screen locked) is credited on return.
 *
 * @example
 * const { time, isRunning, start, stop, pause, resume, reset } = useTimer();
 */
export interface UseTimerOptions {
  /** Called at autoSaveInterval intervals; useful for persisting time to backend. */
  onUpdate?: (time: number) => void;
  /** How often onUpdate is called, in seconds (default: 30). */
  autoSaveInterval?: number;
  /** Pause timer on user inactivity (default: true). */
  detectInactivity?: boolean;
  /** Seconds of inactivity before pausing (default: 120 = 2 min). */
  inactivityThreshold?: number;
}

export interface UseTimerReturn {
  time: number;
  isRunning: boolean;
  wasRunning: boolean;
  start: () => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
  getFormattedTime: () => string;
}

export function useTimer(options?: UseTimerOptions): UseTimerReturn {
  const {
    onUpdate,
    autoSaveInterval = 30,
    detectInactivity = true,
    inactivityThreshold = 120,
  } = options || {};

  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [wasRunning, setWasRunning] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSaveTimeRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Audio en segundo plano: el reloj (`setInterval`) se congela al bloquear el
  // móvil, pero el audio sigue sonando. Al ocultarse la pestaña guardamos el
  // `currentTime` de los audios en reproducción; al volver, su avance real es
  // tiempo de escucha que el reloj no pudo contar.
  const hiddenAtRef = useRef<number>(0);
  const audioSnapshotRef = useRef<Map<HTMLMediaElement, number> | null>(null);

  // Refs so callbacks can always read the latest state without closing over it.
  const isRunningRef = useRef(false);
  const wasRunningRef = useRef(false);
  const timeRef = useRef(0);

  useEffect(() => { isRunningRef.current = isRunning; }, [isRunning]);
  useEffect(() => { wasRunningRef.current = wasRunning; }, [wasRunning]);
  useEffect(() => { timeRef.current = time; }, [time]);

  // Update last activity on interaction; resume after inactivity pause (tab visible).
  // Before this, pause() from idle never resumed until visibilitychange — tiempo artificialmente bajo.
  useEffect(() => {
    if (!detectInactivity) return;

    const resumeIfPausedByIdle = () => {
      if (document.hidden || isRunningRef.current || !wasRunningRef.current) return;
      setIsRunning(true);
      setWasRunning(false);
      lastActivityRef.current = Date.now();
    };

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      resumeIfPausedByIdle();
    };

    // Input del usuario (burbujea): cubre leer y navegar (scroll, mousemove…).
    const inputEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    inputEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Reproducción de audio/vídeo como señal de actividad: escuchar es estudiar
    // aunque no haya input. `timeupdate` se dispara ~4 veces/s mientras suena;
    // no burbujea, así que se capta en fase de captura sobre `document`.
    document.addEventListener('timeupdate', updateActivity, { capture: true, passive: true });

    return () => {
      inputEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });
      document.removeEventListener('timeupdate', updateActivity, { capture: true });
    };
  }, [detectInactivity]);

  const stop = useCallback(() => {
    if (onUpdate && timeRef.current > 0) {
      onUpdate(timeRef.current);
    }
    setIsRunning(false);
    setWasRunning(false);
    setTime(0);
    lastSaveTimeRef.current = 0;
  }, [onUpdate]);

  const pause = useCallback(() => {
    if (onUpdate && timeRef.current > 0) {
      onUpdate(timeRef.current);
      lastSaveTimeRef.current = timeRef.current;
    }
    setWasRunning(isRunningRef.current);
    setIsRunning(false);
  }, [onUpdate]);

  const resume = useCallback(() => {
    if (wasRunningRef.current) {
      setIsRunning(true);
      setWasRunning(false);
      lastActivityRef.current = Date.now();
    }
  }, []);

  useEffect(() => {
    if (!detectInactivity) return;

    /** Guarda el `currentTime` de los audios/vídeos que están sonando ahora. */
    const snapshotPlayingMedia = () => {
      const snapshot = new Map<HTMLMediaElement, number>();
      document.querySelectorAll('audio, video').forEach((el) => {
        const media = el as HTMLMediaElement;
        if (!media.paused && !media.ended && media.readyState >= 2) {
          snapshot.set(media, media.currentTime);
        }
      });
      audioSnapshotRef.current = snapshot;
      hiddenAtRef.current = Date.now();
    };

    /**
     * Segundos de audio reproducidos mientras la pestaña estuvo oculta. Sin
     * riesgo de seeks: en segundo plano no hay interacción posible, así que el
     * avance de `currentTime` es reproducción pura. Se capa al tiempo real
     * transcurrido (descarta bucles/recargas y mantiene la semántica de
     * "tiempo real de estudio").
     */
    const consumeBackgroundAudioSeconds = (): number => {
      const snapshot = audioSnapshotRef.current;
      audioSnapshotRef.current = null;
      if (!snapshot || snapshot.size === 0) return 0;
      const hiddenWallSeconds = (Date.now() - hiddenAtRef.current) / 1000;
      let played = 0;
      for (const [media, startTime] of snapshot) {
        const advance = Math.min(Math.max(media.currentTime - startTime, 0), hiddenWallSeconds);
        played = Math.max(played, advance);
      }
      return Math.round(played);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (isRunningRef.current) {
          snapshotPlayingMedia();
          pause();
        }
      } else {
        const backgroundAudio = consumeBackgroundAudioSeconds();
        if (backgroundAudio > 0 && wasRunningRef.current) {
          setTime((prev) => prev + backgroundAudio);
        }
        if (wasRunningRef.current) resume();
        lastActivityRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [detectInactivity, pause, resume]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (inactivityCheckRef.current) clearInterval(inactivityCheckRef.current);
    };
  }, []);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);

      if (detectInactivity) {
        inactivityCheckRef.current = setInterval(() => {
          const timeSinceLastActivity = (Date.now() - lastActivityRef.current) / 1000;
          if (timeSinceLastActivity > inactivityThreshold) {
            pause();
          }
        }, 5000);
      }
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (inactivityCheckRef.current) {
        clearInterval(inactivityCheckRef.current);
        inactivityCheckRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (inactivityCheckRef.current) clearInterval(inactivityCheckRef.current);
    };
  }, [isRunning, detectInactivity, inactivityThreshold, pause]);

  useEffect(() => {
    if (!onUpdate || !isRunning) return;
    if (time > 0 && time - lastSaveTimeRef.current >= autoSaveInterval) {
      onUpdate(time);
      lastSaveTimeRef.current = time;
    }
  }, [time, isRunning, onUpdate, autoSaveInterval]);

  const start = useCallback(() => {
    setTime(0);
    setIsRunning(true);
    setWasRunning(false);
    lastActivityRef.current = Date.now();
  }, []);

  const reset = useCallback(() => {
    setTime(0);
  }, []);

  const getFormattedTime = useCallback(() => {
    const t = timeRef.current;
    const minutes = Math.floor(t / 60);
    const seconds = t % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return { time, isRunning, wasRunning, start, stop, pause, resume, reset, getFormattedTime };
}
